import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from '../../styles/AdminPanel.module.scss';
import { assetUrl } from '../../utils/imageUrl';
import {
  getBestWholesaleTier,
  getWholesaleDiscountPercent,
  hasDiscount,
  hasWholesaleTiers,
  resolveWholesaleTiers,
} from '../../utils/productPrice';
import { downloadExcel } from '../../utils/exportExcel';
import { getStockLabelAdminText } from '../../utils/productStockStatus';

const MAX_PRODUCTS = 10000;

const FILTER_OPTIONS = [
  { id: 'all', label: 'Усі товари' },
  { id: 'inStock', label: 'В наявності' },
  { id: 'outOfStock', label: 'Немає в наявності' },
  { id: 'wholesale', label: 'З оптовою ціною' },
  { id: 'discount', label: 'Зі знижкою' },
];

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('uk-UA', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const exportProductsExcel = (items) => {
  const headers = ['ID', 'Назва', 'Код', 'Категория', 'Ціна', 'Наличие', 'Количество', 'Замовлення'];
  const rows = items.map((p) => [
    p.id,
    p.title || '',
    p.article || '',
    p.category?.name || '',
    p.price,
    getStockLabelAdminText(p.stockLabel, p.inStock),
    p.quantity ?? 0,
    p.purchasesCount ?? 0,
  ]);
  downloadExcel(`products-${new Date().toISOString().slice(0, 10)}`, 'Товари', headers, rows);
};

const ProductsList = ({ products, onEdit, onDelete, onCreate }) => {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [selected, setSelected] = useState([]);
  const [expandedPrices, setExpandedPrices] = useState({});
  const [openMenuId, setOpenMenuId] = useState(null);

  const stats = useMemo(() => {
    const total = products.length;
    const inCatalog = products.filter((p) => p.inStock !== false).length;
    const notInCatalog = total - inCatalog;
    return { total, inCatalog, notInCatalog };
  }, [products]);

  const filteredProducts = useMemo(() => {
    const query = search.trim().toLowerCase();

    let list = products.filter((product) => {
      const matchesSearch = !query || [
        product.title,
        product.article,
        product.category?.name,
        String(product.id),
      ].some((v) => String(v || '').toLowerCase().includes(query));

      if (!matchesSearch) return false;

      switch (filter) {
        case 'inStock':
          return product.inStock !== false;
        case 'outOfStock':
          return product.inStock === false;
        case 'wholesale':
          return hasWholesaleTiers(product);
        case 'discount':
          return hasDiscount(product.price, product.oldPrice);
        default:
          return true;
      }
    });

    list = [...list].sort((a, b) => {
      let cmp = 0;
      switch (sortBy) {
        case 'title':
          cmp = String(a.title).localeCompare(String(b.title), 'uk');
          break;
        case 'code':
          cmp = String(a.article || '').localeCompare(String(b.article || ''), 'uk');
          break;
        case 'price':
          cmp = Number(a.price) - Number(b.price);
          break;
        case 'orders':
          cmp = Number(a.purchasesCount || 0) - Number(b.purchasesCount || 0);
          break;
        case 'date':
        default:
          cmp = new Date(a.updatedAt || a.createdAt || 0) - new Date(b.updatedAt || b.createdAt || 0);
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return list;
  }, [products, search, filter, sortBy, sortDir]);

  const allSelected = filteredProducts.length > 0 && selected.length === filteredProducts.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected([]);
    } else {
      setSelected(filteredProducts.map((p) => p.id));
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const handleExport = () => {
    const items = selected.length
      ? filteredProducts.filter((p) => selected.includes(p.id))
      : filteredProducts;
    exportProductsExcel(items);
  };

  const togglePriceExpand = (id) => {
    setExpandedPrices((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (!products.length) {
    return (
      <div className={styles.productsListPage}>
        <div className={styles.productsListHeader}>
          <h2 className={styles.productsListTitle}>Список позицій</h2>
          <button type="button" className={styles.productsListAddBtn} onClick={onCreate}>
            Додати товар
          </button>
        </div>
        <div className={styles.empty}>Товари отсутствуют</div>
      </div>
    );
  }

  return (
    <div className={styles.productsListPage}>
      <div className={styles.productsListHeader}>
        <h2 className={styles.productsListTitle}>Список позицій</h2>
        <div className={styles.productsListHeaderActions}>
          <button type="button" className={styles.productsListExportBtn} onClick={handleExport}>
            Експорт в Excel
          </button>
          <button type="button" className={styles.productsListAddBtn} onClick={onCreate}>
            Додати товар
          </button>
        </div>
      </div>

      <div className={styles.productsListStats}>
        <div className={styles.productsListStat}>
          <span className={styles.productsListStatLabel}>Додано</span>
          <strong>{stats.total} / {MAX_PRODUCTS}</strong>
        </div>
        <div className={styles.productsListStat}>
          <span className={styles.productsListStatLabel}>Опубліковано</span>
          <strong>{stats.total}</strong>
        </div>
        <div className={styles.productsListStat}>
          <span className={styles.productsListStatLabel}>В каталоге</span>
          <strong>{stats.inCatalog} / {stats.total}</strong>
        </div>
        <div className={`${styles.productsListStat} ${styles.productsListStatWarn}`}>
          <span className={styles.productsListStatLabel}>Не в каталоге</span>
          <strong>{stats.notInCatalog} / {stats.total}</strong>
        </div>
      </div>

      <div className={styles.productsListToolbar}>
        <select
          className={styles.productsListFilterSelect}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          {FILTER_OPTIONS.map((opt) => (
            <option key={opt.id} value={opt.id}>{opt.label}</option>
          ))}
        </select>
        <div className={styles.productsListSearch}>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Пошук за назвою, кодом, категорією..."
          />
        </div>
        <div className={styles.productsListSavedFilter}>
          Збережені фільтри: {FILTER_OPTIONS.find((o) => o.id === filter)?.label} ({filteredProducts.length})
        </div>
      </div>

      <div className={styles.productsTableWrap}>
        <table className={styles.productsTable}>
          <thead>
            <tr>
              <th className={styles.productsTableCheck}>
                <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
              </th>
              <th />
              <th>
                <button type="button" className={styles.productsTableSort} onClick={() => toggleSort('title')}>
                  Назва {sortBy === 'title' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </button>
              </th>
              <th>
                <button type="button" className={styles.productsTableSort} onClick={() => toggleSort('date')}>
                  Дата {sortBy === 'date' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </button>
              </th>
              <th>
                <button type="button" className={styles.productsTableSort} onClick={() => toggleSort('code')}>
                  Код {sortBy === 'code' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </button>
              </th>
              <th>Відображення</th>
              <th>
                <button type="button" className={styles.productsTableSort} onClick={() => toggleSort('price')}>
                  Ціна {sortBy === 'price' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </button>
              </th>
              <th>
                <button type="button" className={styles.productsTableSort} onClick={() => toggleSort('orders')}>
                  Замовлення {sortBy === 'orders' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </button>
              </th>
              <th>Дії</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.length === 0 ? (
              <tr>
                <td colSpan={9} className={styles.productsTableEmpty}>
                  За вашим запитом нічого не знайдено
                </td>
              </tr>
            ) : (
              filteredProducts.map((product) => {
                const tiers = resolveWholesaleTiers(product);
                const bestTier = getBestWholesaleTier(product);
                const discountPct = bestTier
                  ? getWholesaleDiscountPercent(product.price, bestTier.price)
                  : null;
                const giftCount = product.giftProductIds?.length || 0;
                const isExpanded = expandedPrices[product.id];

                return (
                  <tr key={product.id} className={selected.includes(product.id) ? styles.productsTableSelected : ''}>
                    <td className={styles.productsTableCheck}>
                      <input
                        type="checkbox"
                        checked={selected.includes(product.id)}
                        onChange={() => toggleSelect(product.id)}
                      />
                    </td>
                    <td className={styles.productsTablePreview}>
                      <div className={styles.productsTablePreviewBox}>
                        <span
                          className={`${styles.productsTableStatusBar} ${
                            product.inStock !== false ? styles.productsTableStatusIn : styles.productsTableStatusOut
                          }`}
                        />
                        <img src={assetUrl(product.images?.[0] || '')} alt={product.title} />
                      </div>
                    </td>
                    <td className={styles.productsTableName}>
                      <button type="button" className={styles.productsTableTitle} onClick={() => onEdit(product)}>
                        {product.title}
                      </button>
                      <div className={styles.productsTableCategory}>
                        {product.category?.name || 'Без категорії'}
                      </div>
                      <div className={styles.productsTableTags}>
                        {giftCount > 0 && (
                          <span className={styles.productsTableTag}>Подарунки: {giftCount}</span>
                        )}
                        {hasWholesaleTiers(product) && (
                          <span className={styles.productsTableTag}>Опт</span>
                        )}
                        {hasDiscount(product.price, product.oldPrice) && (
                          <span className={styles.productsTableTag}>Знижка</span>
                        )}
                      </div>
                    </td>
                    <td className={styles.productsTableDate}>
                      {formatDate(product.updatedAt || product.createdAt)}
                    </td>
                    <td className={styles.productsTableCode}>
                      {product.article || product.id}
                    </td>
                    <td className={styles.productsTableDisplay}>
                      <div className={product.inStock !== false ? styles.stockIn : styles.stockOut}>
                        {getStockLabelAdminText(product.stockLabel, product.inStock)}
                      </div>
                      <div className={styles.published}>Опубліковано</div>
                      <div className={styles.channelIcons}>
                        <span className={styles.channelOn} title="На сайті">👁</span>
                        <span className={product.inStock !== false ? styles.channelOn : styles.channelOff} title="В кошик">
                          🛒
                        </span>
                      </div>
                    </td>
                    <td className={styles.productsTablePrice}>
                      <div className={styles.priceMain}>
                        {product.price} ₴
                        {hasDiscount(product.price, product.oldPrice) && (
                          <span className={styles.priceOld}>{product.oldPrice} ₴</span>
                        )}
                      </div>
                      {tiers.length > 0 && (
                        <>
                          {tiers.length > 1 && (
                            <button
                              type="button"
                              className={styles.priceMoreLink}
                              onClick={() => togglePriceExpand(product.id)}
                            >
                              {isExpanded ? 'Приховати ціни' : `Ще ${tiers.length - 1} цін${tiers.length === 2 ? 'а' : tiers.length < 5 ? 'и' : ''}`}
                            </button>
                          )}
                          <div className={styles.priceTypeLabel}>Роздріб і опт</div>
                          {bestTier && discountPct && (
                            <div className={styles.priceWholesaleBadge}>
                              <span>🛒</span>
                              <span>-{discountPct}%, {bestTier.price} ₴</span>
                              <small>від {bestTier.minQty} шт.</small>
                            </div>
                          )}
                          {isExpanded && tiers.length > 1 && (
                            <div className={styles.priceTiersList}>
                              {tiers.map((tier) => (
                                <div key={tier.minQty} className={styles.priceTierRow}>
                                  від {tier.minQty} шт. — {tier.price} ₴
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </td>
                    <td className={styles.productsTableOrders}>
                      {product.purchasesCount ?? 0}
                    </td>
                    <td className={styles.productsTableActions}>
                      <div className={styles.actionsMenu}>
                        <button
                          type="button"
                          className={styles.actionsMenuBtn}
                          onClick={() => setOpenMenuId(openMenuId === product.id ? null : product.id)}
                        >
                          Дії ▾
                        </button>
                        {openMenuId === product.id && (
                          <div className={styles.actionsMenuList}>
                            <button type="button" onClick={() => { onEdit(product); setOpenMenuId(null); }}>
                              Редагувати
                            </button>
                            <Link to={`/products/${product.id}`} onClick={() => setOpenMenuId(null)}>
                              Відкрити на сайті
                            </Link>
                            <button type="button" onClick={() => { onDelete(product); setOpenMenuId(null); }}>
                              Видалити
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.productsListFooter}>
        Показано {filteredProducts.length} з {products.length}
        {selected.length > 0 && ` · Обрано: ${selected.length}`}
      </div>
    </div>
  );
};

export default ProductsList;
